import { Alert, Button, colors, ModalActions, ModalHeader, Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-ring.svg';
import { ReactComponent as DownloadIcon } from '@ui-components/neo.tax/dist/icons/download.svg';
import { ReactElement, useEffect } from 'react';

import { useSupervisorRdAnalysisPreview } from '@neo.tax/rd/hooks';

import { StyledContentStack, StyledModal } from './bulk-apply-supervisor-rd-percentage-modal.styled';
import { useBulkApplySupervisorRdPercentageAnalyticIds } from './useBulkApplySupervisorRdPercentageAnalyticIds';

type BulkApplySupervisorRdPercentageModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  fiscalYearId: string;
  selectedExpenseIds: string[];
  isAllSelected?: boolean;
  filter?: Record<string, unknown>;
  onApplySuccess?: () => void;
};

export const BulkApplySupervisorRdPercentageModal = ({
  filter,
  fiscalYearId,
  isAllSelected,
  isOpen,
  onApplySuccess,
  selectedExpenseIds,
  setIsOpen,
}: BulkApplySupervisorRdPercentageModalProps): ReactElement | null => {
  const {
    applied,
    applyChanges,
    applying,
    downloadPreview,
    error,
    generatePreview,
    loading,
    previewGenerated,
    reset,
    supervisorCount,
  } = useSupervisorRdAnalysisPreview(fiscalYearId);
  const analyticIds = useBulkApplySupervisorRdPercentageAnalyticIds();

  // Reset state when modal opens or selected expenses change
  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, selectedExpenseIds, reset]);

  const handleGeneratePreview = async (): Promise<void> => {
    await generatePreview(selectedExpenseIds, isAllSelected, filter);
  };

  const handleDownloadPreview = (): void => {
    downloadPreview();
  };

  const handleApplyChanges = async (): Promise<void> => {
    const success = await applyChanges(selectedExpenseIds, isAllSelected, filter);
    if (success) {
      onApplySuccess?.();
      setIsOpen(false);
    }
  };

  const total = selectedExpenseIds.length;

  return (
    <StyledModal isOpen={isOpen} setIsOpen={setIsOpen}>
      <Stack>
        <Stack fullWidth direction="column" paddingX={6} paddingY={6}>
          <ModalHeader
            icon={<AlarmBellRing width={24} color={colors.blue[600]} />}
            iconBgColor={colors.blue[50]}
            title={
              <Typography fontSize="base" fontWeight="semibold">
                {previewGenerated && supervisorCount > 0
                  ? `Set Supervisor R&D % for ${supervisorCount} ${
                      supervisorCount === 1 ? 'Supervisor' : 'Supervisors'
                    }`
                  : isAllSelected
                    ? 'Set Supervisor R&D % for all Wage QREs'
                    : `Set Supervisor R&D % for ${total} ${total === 1 ? 'Wage QRE' : 'Wage QREs'}`}
              </Typography>
            }
            copy={
              <Stack gap={6} paddingY={4} direction="column">
                <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
                  This calculates the R&D% for supervisors based on the average R&D% of the people they directly manage.
                </Typography>
                {isAllSelected && (
                  <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
                    All wage QREs are selected, including those not currently visible on the page. The supervisor
                    formula will be applied across all of them.
                  </Typography>
                )}
              </Stack>
            }
          />

          <StyledContentStack direction="column" fullWidth gap={4} paddingY={4}>
            <Stack direction="row" gap={3} alignItems="center">
              <Button
                disabled={loading || previewGenerated}
                id={analyticIds.generatePreviewBtnId}
                variant="secondary"
                rounded="regular"
                size="base"
                onClick={handleGeneratePreview}
              >
                {loading ? 'Generating...' : 'Generate Preview CSV'}
              </Button>

              {previewGenerated && supervisorCount > 0 && (
                <Button
                  disabled={loading}
                  id={analyticIds.downloadPreviewBtnId}
                  variant="white"
                  rounded="regular"
                  size="sm"
                  onClick={handleDownloadPreview}
                >
                  <Stack direction="row" gap={2} alignItems="center">
                    <DownloadIcon width={16} height={16} />
                    <span>Download Preview</span>
                  </Stack>
                </Button>
              )}
            </Stack>

            {previewGenerated && supervisorCount === 0 && (
              <Alert
                severity="warning"
                title="No supervisors found in the selected expenses. Only employees with direct reports can have their R&D % calculated using this formula."
              />
            )}

            {previewGenerated && supervisorCount > 0 && !applied && (
              <Alert
                severity="success"
                title={`Found ${supervisorCount} supervisor${
                  supervisorCount === 1 ? '' : 's'
                }. Download and review the CSV to see the calculated R&D percentages.`}
              />
            )}

            {applied && (
              <Alert
                severity="success"
                title="Supervisor R&D percentages are being applied. The page will update when complete."
              />
            )}

            {error && <Alert severity="error" title={error} />}

            {!previewGenerated && !applied && !error && (
              <Alert
                severity="info"
                title="Generate a preview to see the calculated R&D percentages before applying changes."
              />
            )}
          </StyledContentStack>

          <ModalActions hasTopBorder={false}>
            <Button
              disabled={loading}
              id={analyticIds.cancelBtnId}
              variant="white"
              rounded="regular"
              size="base"
              onClick={(): void => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!previewGenerated || supervisorCount === 0 || applying || applied}
              id={analyticIds.applyBtnId}
              variant="primary"
              rounded="regular"
              size="base"
              onClick={handleApplyChanges}
            >
              {applying ? 'Applying...' : 'Apply Changes'}
            </Button>
          </ModalActions>
        </Stack>
      </Stack>
    </StyledModal>
  );
};
